import { Toast } from "@base-ui/react/toast";
import type { ReactNode } from "react";

/**
 * One manager for the app, outside React, so any component (or a mutation callback that
 * outlives its component) can confirm an action. Base UI handles the live region, pausing
 * on hover and swipe to dismiss.
 */
const manager = Toast.createToastManager();

type Tone = "ok" | "error";

const notify = (message: string, tone: Tone = "ok") =>
  manager.add({ description: message, type: tone, timeout: tone === "error" ? 6000 : 3500 });

export const useToast = () => notify;

function Toasts() {
  const { toasts } = Toast.useToastManager();
  return toasts.map((toast) => (
    <Toast.Root
      key={toast.id}
      toast={toast}
      className={`absolute right-0 bottom-0 left-0 mx-auto w-fit max-w-full rounded-full px-5 py-3 font-bold shadow-xl transition-all duration-300 [transform:translateY(calc(var(--toast-index)*-110%))] data-[ending-style]:opacity-0 data-[starting-style]:translate-y-4 data-[starting-style]:opacity-0 ${
        toast.type === "error" ? "bg-danger text-white" : "bg-[#1f2544] text-white"
      }`}
    >
      <Toast.Content className="flex items-center gap-3">
        <Toast.Description />
        <Toast.Close
          className="-mr-2 grid size-7 place-items-center rounded-full text-lg hover:bg-white/15"
          aria-label="Fermer"
        >
          ×
        </Toast.Close>
      </Toast.Content>
    </Toast.Root>
  ));
}

export function ToastProvider({ children }: { children: ReactNode }) {
  return (
    <Toast.Provider toastManager={manager}>
      {children}
      <Toast.Portal>
        <Toast.Viewport className="fixed inset-x-4 bottom-5 z-[60] mx-auto h-0 max-w-lg">
          <Toasts />
        </Toast.Viewport>
      </Toast.Portal>
    </Toast.Provider>
  );
}
